import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Building2, Store, AlertCircle } from "lucide-react";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { TopBarSection } from "@/components/shared/SecaoBarraSuperior";
import { NumberKpiCard } from "@/components/shared/NumberKpiCard";

import { ClienteListTable } from "./components/ClienteListTable";
import { ClienteCreateFormDialog } from "./components/ClienteCreateFormDialog";
import { FilialPendenteListTable } from "./components/FilialPendenteListTable";
import { FilialListTable } from "./components/FilialListTable";

import { useClienteKpis } from "@/hooks/clientes/use-cliente-kpis";

// 1. Extração de dados estáticos (Clean Code)
const TAB_CONFIG = [
  {
    id: "pendentes",
    icon: AlertCircle,
    label: "Filiais Pendentes",
    description: "Ação Necessária: Filiais importadas aguardando vínculo com Cliente.",
  },
  {
    id: "clientes",
    icon: Building2,
    label: "Clientes",
    description: "Gerenciamento dos clientes principais do sistema.",
  },
  {
    id: "filiais",
    icon: Store,
    label: "Todas as Filiais",
    description: "Listagem geral de filiais e seus vínculos.",
  },
] as const;

export function ClienteAdminListPage() {
  // 2. Sincronização com a URL via query params (?tab=...)
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "pendentes";

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const { resumo, isLoading: loadingKpis } = useClienteKpis();

  // Altera a aba atualizando a URL sem empilhar no histórico do navegador (replace: true)
  const handleTabChange = (value: string) => {
    setSearchParams(
      (prev) => {
        prev.set("tab", value);
        return prev;
      },
      { replace: true },
    );
  };

  // 3. Helper para facilitar a leitura no JSX
  const activeTabConfig = TAB_CONFIG.find((tab) => tab.id === activeTab) || TAB_CONFIG[0];

  return (
    <div className="flex flex-col space-y-6 pb-10 duration-500 animate-in fade-in">
      <TopBarSection
        entity="Cliente"
        title="Gestão de Clientes"
        extraActions={[
          <Button
            key="create"
            size="sm"
            onClick={() => setIsCreateModalOpen(true)}
            className="gap-2 bg-purple-600 text-white shadow-sm hover:bg-purple-700"
          >
            <Building2 className="h-4 w-4" />
            Novo Cliente
          </Button>,
        ]}
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <NumberKpiCard
          title="Aguardando Vínculo"
          value={resumo.filiaisPendentes}
          icon={AlertCircle}
          variant={resumo.filiaisPendentes > 0 ? "destructive" : "emerald"}
          loading={loadingKpis}
        />
        <NumberKpiCard
          title="Clientes Cadastrados"
          value={resumo.totalClientes}
          icon={Building2}
          variant="purple"
          loading={loadingKpis}
        />
        <NumberKpiCard
          title="Total de Filiais"
          value={resumo.totalFiliais}
          icon={Store}
          variant="blue"
          loading={loadingKpis}
        />
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-4">
        <div className="flex flex-col items-center justify-between gap-4 rounded-xl border border-border bg-card p-1.5 shadow-sm sm:flex-row">
          <TabsList className="h-9 bg-transparent">
            {TAB_CONFIG.map((config) => (
              <TabsTrigger
                key={config.id}
                value={config.id}
                className="gap-2 px-6 data-[state=active]:text-purple-600 dark:data-[state=active]:text-purple-400"
              >
                <config.icon className="h-4 w-4" />
                {config.label}
                {config.id === "pendentes" && resumo.filiaisPendentes > 0 && (
                  <span className="ml-1 rounded-full bg-destructive/10 px-2 py-0.5 text-[10px] font-bold text-destructive">
                    {resumo.filiaisPendentes}
                  </span>
                )}
              </TabsTrigger>
            ))}
          </TabsList>

          <div className="hidden h-6 border-l border-border px-4 md:block">
            <p className="text-xs font-medium text-muted-foreground">
              {activeTabConfig?.description}
            </p>
          </div>
        </div>

        {/* 4. Separação visual clara dos conteúdos */}
        <TabsContent value="pendentes" className="mt-0 outline-none">
          <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
            <FilialPendenteListTable storageKey="filiais-pendentes-v1" />
          </div>
        </TabsContent>

        <TabsContent value="clientes" className="mt-0 outline-none">
          <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
            <ClienteListTable storageKey="clientes-cliente-v1" />
          </div>
        </TabsContent>

        <TabsContent value="filiais" className="mt-0 outline-none">
          <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
            <FilialListTable storageKey="filiais-all-v1" />
          </div>
        </TabsContent>
      </Tabs>

      <ClienteCreateFormDialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen} />
    </div>
  );
}
